// src/components/PatientConditionsModal.tsx

import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Modal, ModalContent, CloseButton, Button } from '../styles/GlobalStyles';
import { PatientSpecialConditions } from '../types';
import { useTranslation } from '../i18n';

const FormSection = styled.div`
  margin-bottom: 32px;
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SectionIcon = styled.span`
  font-size: 18px;
`;

const RadioGroup = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
`;

const RadioItem = styled.label<{ isSelected: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border: 2px solid ${props => props.isSelected ? '#0369a1' : '#e5e7eb'};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: ${props => props.isSelected ? '#f0f9ff' : 'white'};

  &:hover {
    border-color: #0369a1;
    background-color: #f8fafc;
  }
`;

const RadioButton = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

const RadioLabel = styled.span`
  font-size: 14px;
  color: #374151;
  font-weight: 500;
  flex: 1;
`;

const TextInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.2s ease;
  
  &:focus {
    border-color: #0369a1;
    box-shadow: 0 0 0 3px rgba(3, 105, 161, 0.1);
    outline: none;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 32px;
`;

const Summary = styled.div`
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 24px;
`;

const SummaryTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
`;

const SummaryContent = styled.div`
  font-size: 13px;
  color: #6b7280;
  line-height: 1.5;
`;

const UnknownButton = styled.button<{ isSelected: boolean }>`
  padding: 8px 16px;
  border: 2px solid ${props => props.isSelected ? '#f59e0b' : '#e5e7eb'};
  border-radius: 6px;
  background-color: ${props => props.isSelected ? '#fef3c7' : 'white'};
  color: ${props => props.isSelected ? '#d97706' : '#6b7280'};
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 8px;

  &:hover {
    border-color: #f59e0b;
    background-color: #fef3c7;
  }
`;

// 🔥 수정: 하드코딩된 텍스트 대신 i18n 키(key) 배열을 사용
const BREAST_SURGERY_CONDITION_KEYS = {
  resectionType: ['totalMastectomy', 'conservingSurgery'],
  cancerStage: ['stage0', 'stage1', 'stage2', 'stage3', 'stage4'],
  reconstructionType: ['none', 'immediate', 'implant', 'autologous'],
};

interface PatientConditionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (conditions: PatientSpecialConditions) => void;
  initialConditions?: PatientSpecialConditions;
}

const PatientConditionsModal: React.FC<PatientConditionsModalProps> = ({ isOpen, onClose, onSave, initialConditions }) => {
  const { t } = useTranslation();

  const [resectionType, setResectionType] = useState('');
  const [cancerStage, setCancerStage] = useState('');
  const [reconstructionType, setReconstructionType] = useState('');
  const [otherNotes, setOtherNotes] = useState('');
  const [unknownStates, setUnknownStates] = useState<Record<string, boolean>>({});

  // 모달이 열릴 때 상태를 초기화하는 useEffect
  useEffect(() => {
    if (isOpen) {
      setResectionType(initialConditions?.resectionType || '');
      setCancerStage(initialConditions?.cancerStage || '');
      setReconstructionType(initialConditions?.reconstructionType || '');
      setOtherNotes(initialConditions?.otherNotes || '');
      setUnknownStates(initialConditions?.unknownStates || {
        resectionType: false,
        cancerStage: false,
        reconstructionType: false,
      });
    }
  }, [isOpen, initialConditions]);

  const handleUnknownToggle = (category: string) => {
    const isNowUnknown = !unknownStates[category];
    setUnknownStates(prev => ({ ...prev, [category]: isNowUnknown }));

    if (isNowUnknown) {
      if (category === 'resectionType') setResectionType('');
      if (category === 'cancerStage') setCancerStage('');
      if (category === 'reconstructionType') setReconstructionType('');
    }
  };

  const handleSave = () => {
    onSave({ resectionType, cancerStage, reconstructionType, otherNotes, unknownStates });
    onClose();
  };

  const handleClose = () => {
    onClose();
  };

  const getTotalSelectedCount = () => {
    let count = 0;
    if (resectionType) count++;
    if (cancerStage) count++;
    if (reconstructionType) count++;
    if (otherNotes.trim()) count++;
    count += Object.values(unknownStates).filter(Boolean).length;
    return count;
  };

  if (!isOpen) return null;

  return (
    <Modal onClick={handleClose}>
      <ModalContent onClick={e => e.stopPropagation()} style={{ width: '90vw', maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto' }}>
        <CloseButton onClick={handleClose}>×</CloseButton>
        <h2 style={{ marginBottom: '24px', fontSize: '20px', fontWeight: '600' }}>
          {t('patientCond.modalTitle')}
        </h2>

        <Summary>
          <SummaryTitle>{t('patientCond.infoTitle')}</SummaryTitle>
          <SummaryContent>
            {t('patientCond.infoContent_line1')}
            <br />
            {t('patientCond.infoContent_line2')}
            <br />
            <strong>{t('patientCond.infoContent_line3', { count: getTotalSelectedCount() })}</strong>
          </SummaryContent>
        </Summary>

        {/* 1. 절제 범위 */}
        <FormSection>
          <SectionTitle>
            {t('patientCond.resectionType')}
          </SectionTitle>
          <RadioGroup>
            {BREAST_SURGERY_CONDITION_KEYS.resectionType.map(optionKey => (
              <RadioItem 
                key={optionKey}
                isSelected={resectionType === optionKey && !unknownStates.resectionType}
              >
                <RadioButton
                  type="radio"
                  name="resectionType"
                  checked={resectionType === optionKey && !unknownStates.resectionType}
                  onChange={() => {
                    setResectionType(optionKey);
                    setUnknownStates(prev => ({ ...prev, resectionType: false }));
                  }}
                />
                <RadioLabel>{t(`patientCond.resectionTypeOptions.${optionKey}`)}</RadioLabel>
              </RadioItem>
            ))}
          </RadioGroup>
          <UnknownButton
            type="button"
            isSelected={unknownStates.resectionType}
            onClick={() => handleUnknownToggle('resectionType')}
          >
            {unknownStates.resectionType ? t('patientCond.unknown_selected') : t('patientCond.unknown')}
          </UnknownButton>
        </FormSection>

        {/* 2. 암 병기 */}
        <FormSection>
          <SectionTitle>
            {t('patientCond.cancerStage')}
          </SectionTitle>
          <RadioGroup>
            {BREAST_SURGERY_CONDITION_KEYS.cancerStage.map(optionKey => (
              <RadioItem 
                key={optionKey}
                isSelected={cancerStage === optionKey && !unknownStates.cancerStage}
              >
                <RadioButton
                  type="radio"
                  name="cancerStage"
                  checked={cancerStage === optionKey && !unknownStates.cancerStage}
                  onChange={() => {
                    setCancerStage(optionKey);
                    setUnknownStates(prev => ({ ...prev, cancerStage: false }));
                  }}
                />
                <RadioLabel>{t(`patientCond.cancerStageOptions.${optionKey}`)}</RadioLabel>
              </RadioItem>
            ))}
          </RadioGroup>
          <UnknownButton
            type="button"
            isSelected={unknownStates.cancerStage}
            onClick={() => handleUnknownToggle('cancerStage')}
          >
            {unknownStates.cancerStage ? t('patientCond.unknown_selected') : t('patientCond.unknown')}
          </UnknownButton>
        </FormSection>

        {/* 3. 유방 재건 여부 */}
        <FormSection>
          <SectionTitle>
            {t('patientCond.reconstructionType')}
          </SectionTitle>
          <RadioGroup>
            {BREAST_SURGERY_CONDITION_KEYS.reconstructionType.map(optionKey => (
              <RadioItem 
                key={optionKey}
                isSelected={reconstructionType === optionKey && !unknownStates.reconstructionType}
              >
                <RadioButton
                  type="radio"
                  name="reconstructionType"
                  checked={reconstructionType === optionKey && !unknownStates.reconstructionType}
                  onChange={() => {
                    setReconstructionType(optionKey);
                    setUnknownStates(prev => ({ ...prev, reconstructionType: false }));
                  }}
                />
                <RadioLabel>{t(`patientCond.reconstructionTypeOptions.${optionKey}`)}</RadioLabel>
              </RadioItem>
            ))}
          </RadioGroup>
          <UnknownButton
            type="button"
            isSelected={unknownStates.reconstructionType}
            onClick={() => handleUnknownToggle('reconstructionType')}
          >
            {unknownStates.reconstructionType ? t('patientCond.unknown_selected') : t('patientCond.unknown')}
          </UnknownButton>
        </FormSection>

        {/* 4. 기타 특이사항 텍스트 입력 */}
        <FormSection>
          <SectionTitle>
            {t('patientCond.otherNotes')}
          </SectionTitle>
          <TextInput
            type="text"
            placeholder={t('patientCond.otherNotes_placeholder')}
            value={otherNotes}
            onChange={(e) => setOtherNotes(e.target.value)}
          />
        </FormSection>

        <ButtonGroup>
          <Button onClick={handleClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" onClick={handleSave}>
            {t('patientCond.save')}
          </Button>
        </ButtonGroup>
      </ModalContent>
    </Modal>
  );
};

export default PatientConditionsModal;